import { useCallback } from "react";
import useSWR, { mutate } from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import { InsertProjectBody, Project, SearchAdminProject, SearchProject, UpdateProjectBody } from "./types/projects";

export const useProjectsApi = () => {
  const api = useApi();

  const useAdminProjects = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/projects?include_shared=1", query);
      return useSWR<Paginated<SearchAdminProject>, CustomError, string>(url, () =>
        api.get<Paginated<SearchAdminProject>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminProject = useCallback(
    (signal: AbortSignal) => {
      const url = "/admin/projects";
      return useSWRMutation<Project, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<Project>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const getProject = useCallback(
    (signal: AbortSignal) => {
      const url = "/projects";
      return useSWRMutation<Project, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<Project>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const getMyProjects = useCallback(
    (signal: AbortSignal) => {
      const url = "/projects";
      return useSWRMutation<SearchProject[], CustomError, string>(url, () =>
        api.get<Paginated<SearchProject>>({ url, signal }).then((x) => x.items),
      );
    },
    [api],
  );

  const useMyProjects = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/projects?owned_only=1", query);
      return useSWR<Paginated<SearchProject>, CustomError, string>(url, () =>
        api.get<Paginated<SearchProject>>({ url, signal }),
      );
    },
    [api],
  );

  const useSharedProjects = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/projects/shared", query);
      return useSWR<Paginated<SearchProject>, CustomError, string>(url, () =>
        api.get<Paginated<SearchProject>>({ url, signal }),
      );
    },
    [api],
  );

  const insertProject = useCallback(
    async (body: InsertProjectBody): Promise<{ id: string }> => {
      const url = `/projects`;
      const id = await api.post<{ id: string }, InsertProjectBody>(url, body);
      mutateCache(["/projects", "/admin/projects"]);
      return id;
    },
    [api],
  );

  const updateProject = useCallback(
    async (projectId: string, body: UpdateProjectBody, optimisticData: Project, isAdmin?: boolean): Promise<void> => {
      const url = `${isAdmin ? "/admin" : ""}/projects/${projectId}`;
      await api.put<UpdateProjectBody>(url, body);
      mutate(url, optimisticData, { revalidate: false, populateCache: true });
      mutateCache(["/projects", "/admin/projects"], url);
    },
    [api],
  );

  const mutateMyProjects = useCallback(
    (query: PaginationQuery, optimisticData: Paginated<SearchProject>) => {
      const url = buildUrl("/projects?owned_only=1", query);
      mutate(url, optimisticData, { revalidate: false, optimisticData: true });
    },
    [api],
  );

  const deleteProject = useCallback(
    async (projectId: string, isAdmin?: boolean): Promise<void> => {
      const url = `${isAdmin ? "/admin" : ""}/projects/${projectId}`;
      await api.del(url);
      mutateCache(["/projects", `/projects/${projectId}`, "/admin/projects", `/admin/projects/${projectId}`]);
    },
    [api],
  );

  const updateProjectStatus = useCallback(
    async (projectId: string, status: string, isAdmin?: boolean): Promise<void> => {
      const url = `${isAdmin ? "/admin" : ""}/projects/${projectId}/status`;
      await api.put<{ status: string }>(url, { status });
      mutateCache([`/projects/${projectId}`, `/admin/projects/${projectId}`]);
    },
    [api],
  );

  const downloadEServiceExportConfigurationsItemsList = useCallback(
    (projectId: string, isAdmin?: boolean) => {
      return api.downloadExcel(`${isAdmin ? "/admin" : ""}/projects/${projectId}/items`, "E-Service import.xlsx");
    },
    [api],
  );

  const removeSharedProject = useCallback(
    async (projectId: string): Promise<void> => {
      await api.del(`/projects/${projectId}/shared`);
      mutateCache(["/projects/shared", `/projects/${projectId}`]);
    },
    [api],
  );

  return {
    useAdminProjects,
    getAdminProject,
    getProject,
    getMyProjects,
    useMyProjects,
    useSharedProjects,
    insertProject,
    updateProject,
    mutateMyProjects,
    deleteProject,
    updateProjectStatus,
    downloadEServiceExportConfigurationsItemsList,
    removeSharedProject,
  };
};
